import React, { useContext, useEffect, useState } from "react";
import { toast } from "react-toastify";
import {
  Button,
  IconButton,
  InputAdornment,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
} from "@material-ui/core";
import { DeleteOutline, Edit, Search } from "@material-ui/icons";
import { makeStyles } from "@material-ui/core/styles";
import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import MainHeaderButtonsWrapper from "../../components/MainHeaderButtonsWrapper";
import Title from "../../components/Title";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { AuthContext } from "../../context/Auth/AuthContext";

const emptyTag = { name: "", color: "#2563eb" };

const useStyles = makeStyles((theme) => ({
  tagForm: {
    display: "grid",
    gridTemplateColumns: "minmax(220px, 1fr) minmax(136px, 160px) max-content",
    alignItems: "center",
    gap: theme.spacing(1.5),
    minWidth: 0,
    "& > *": { minWidth: 0 },
    "& .MuiButton-root": { whiteSpace: "nowrap", minHeight: 40 },
    "@media (max-width: 720px)": {
      gridTemplateColumns: "1fr 1fr",
      "& .MuiButton-root": { gridColumn: "1 / -1", justifySelf: "start" },
    },
    "@media (max-width: 420px)": {
      gridTemplateColumns: "1fr",
      "& .MuiButton-root": { gridColumn: "auto", width: "100%" },
    },
  },
  tagName: { minWidth: 0 },
  tagColor: { minWidth: 136 },
}));

const Tags = () => {
  const classes = useStyles();
  const [tags, setTags] = useState([]);
  const [searchParam, setSearchParam] = useState("");
  const [form, setForm] = useState(emptyTag);
  const [editingId, setEditingId] = useState(null);
  const { isAuth } = useContext(AuthContext);

  const loadTags = async () => {
    try {
      const { data } = await api.get("/crm/tags/", { params: { searchParam } });
      setTags(data.tags || []);
    } catch (error) {
      toastError(error);
    }
  };

  useEffect(() => {
    if (!isAuth) return undefined;
    const timeoutId = setTimeout(loadTags, 250);
    return () => clearTimeout(timeoutId);
  }, [searchParam, isAuth]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    try {
      if (editingId) {
        await api.put(`/crm/tags/${editingId}`, form);
        toast.success("Etiqueta actualizada.");
      } else {
        await api.post("/crm/tags", form);
        toast.success("Etiqueta creada.");
      }
      setForm(emptyTag);
      setEditingId(null);
      await loadTags();
    } catch (error) {
      toastError(error);
    }
  };

  const handleEdit = (tag) => {
    setEditingId(tag.id);
    setForm({ name: tag.name, color: tag.color });
  };

  const handleDelete = async (tag) => {
    if (!window.confirm(`¿Eliminar la etiqueta “${tag.name}”?`)) return;
    try {
      await api.delete(`/crm/tags/${tag.id}`);
      toast.success("Etiqueta eliminada.");
      if (editingId === tag.id) {
        setEditingId(null);
        setForm(emptyTag);
      }
      await loadTags();
    } catch (error) {
      toastError(error);
    }
  };

  return (
    <MainContainer>
      <MainHeader>
        <Title>Etiquetas</Title>
        <MainHeaderButtonsWrapper>
          <TextField
            placeholder="Buscar etiqueta"
            type="search"
            value={searchParam}
            onChange={(event) => setSearchParam(event.target.value)}
            InputProps={{ startAdornment: <InputAdornment position="start"><Search /></InputAdornment> }}
          />
        </MainHeaderButtonsWrapper>
      </MainHeader>
      <Paper style={{ padding: 16, marginBottom: 16 }} variant="outlined">
        <form onSubmit={handleSubmit} className={classes.tagForm}>
          <TextField
            required
            fullWidth
            className={classes.tagName}
            label="Nombre de la etiqueta"
            value={form.name}
            onChange={(event) => setForm({ ...form, name: event.target.value })}
            variant="outlined"
            size="small"
          />
          <TextField
            required
            fullWidth
            className={classes.tagColor}
            label="Color"
            type="color"
            value={form.color}
            onChange={(event) => setForm({ ...form, color: event.target.value })}
            variant="outlined"
            size="small"
            InputLabelProps={{ shrink: true }}
          />
          <Button type="submit" color="primary" variant="contained">
            {editingId ? "Guardar cambios" : "Crear etiqueta"}
          </Button>
          {editingId && <Button onClick={() => { setEditingId(null); setForm(emptyTag); }}>Cancelar</Button>}
        </form>
      </Paper>
      <Paper style={{ flex: 1, overflow: "auto" }} variant="outlined">
        <Table size="small">
          <TableHead><TableRow><TableCell>Etiqueta</TableCell><TableCell>Color</TableCell><TableCell align="right">Acciones</TableCell></TableRow></TableHead>
          <TableBody>
            {tags.map((tag) => <TableRow key={tag.id}>
              <TableCell><span style={{ display: "inline-block", width: 10, height: 10, borderRadius: "50%", background: tag.color, marginRight: 8 }} />{tag.name}</TableCell>
              <TableCell>{tag.color}</TableCell>
              <TableCell align="right">
                <IconButton size="small" aria-label={`Editar ${tag.name}`} onClick={() => handleEdit(tag)}><Edit /></IconButton>
                <IconButton size="small" aria-label={`Eliminar ${tag.name}`} onClick={() => handleDelete(tag)}><DeleteOutline /></IconButton>
              </TableCell>
            </TableRow>)}
            {!tags.length && <TableRow><TableCell colSpan={3} align="center">Todavía no hay etiquetas.</TableCell></TableRow>}
          </TableBody>
        </Table>
      </Paper>
    </MainContainer>
  );
};

export default Tags;
